import {
  getLocation,
  listStationDevices,
  provisionStationDevice,
  revokeStationDevice,
} from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type {
  LocationResponse,
  ProvisionStationDeviceResponse,
  StationDeviceSummary,
} from '@jq4race/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { useParams } from '@tanstack/react-router';
import { Copy, TriangleAlert } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';

import { AccessDeniedState } from '../../../../shared/errors/AccessDeniedState';
import { isForbiddenError } from '../../../../shared/errors/isForbiddenError';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { formatDate, formatDateTime } from '../../../../shared/format/format-date';
import { PageHeader } from '../../../app-shell/ui/PageHeader';

const MAX_LABEL_LENGTH = 120;

export function StationDevicesPage() {
  const { t, i18n } = useTranslation('station-devices');
  const { locationId } = useParams({ strict: false });

  const [devices, setDevices] = useState<StationDeviceSummary[]>([]);
  const [location, setLocation] = useState<LocationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  // The plaintext token lives ONLY in this state. It is dropped as soon as
  // the admin dismisses the dialog; the backend can never return it again.
  const [issued, setIssued] = useState<ProvisionStationDeviceResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState<StationDeviceSummary | null>(null);
  const [revoking, setRevoking] = useState(false);

  const reload = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setAccessDenied(false);
    try {
      const [list, loc] = await Promise.all([
        listStationDevices(locationId),
        getLocation(locationId).catch(() => null),
      ]);
      setDevices(list.devices);
      setLocation(loc);
    } catch (e) {
      setAccessDenied(isForbiddenError(e));
      setError(translateApiError(e, t, { fallback: t('feedback.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [locationId, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    const trimmed = label.trim();
    if (!locationId || !trimmed) return;
    setCreating(true);
    setError(null);
    try {
      const result = await provisionStationDevice(locationId, { label: trimmed });
      setIssued(result);
      setCopied(false);
      setLabel('');
      void reload();
    } catch (err) {
      setError(translateApiError(err, t, { fallback: t('feedback.createError') }));
    } finally {
      setCreating(false);
    }
  }

  async function handleCopy() {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.token);
      setCopied(true);
    } catch {
      setError(t('token.copyError'));
    }
  }

  async function handleRevoke() {
    if (!locationId || !revokeTarget) return;
    setRevoking(true);
    setError(null);
    try {
      await revokeStationDevice(locationId, revokeTarget.id);
      setRevokeTarget(null);
      void reload();
    } catch (err) {
      setError(translateApiError(err, t, { fallback: t('feedback.revokeError') }));
    } finally {
      setRevoking(false);
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={location ? t('page.titleWithLocation', { name: location.name }) : t('page.title')}
        description={t('page.description')}
      />

      {error && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
        >
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
          <p className="flex-1 leading-snug">{error}</p>
        </div>
      )}

      {accessDenied ? (
        <AccessDeniedState />
      ) : (
        <>
          <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
            <CardContent className="p-6 sm:p-8">
              <form
                onSubmit={handleCreate}
                className="flex flex-col gap-3 sm:flex-row sm:items-end"
              >
                <div className="flex-1 space-y-2">
                  <Label htmlFor="station-device-label">{t('form.label')}</Label>
                  <Input
                    id="station-device-label"
                    value={label}
                    maxLength={MAX_LABEL_LENGTH}
                    placeholder={t('form.placeholder')}
                    onChange={(ev) => setLabel(ev.target.value)}
                  />
                </div>
                <Button type="submit" size="sm" disabled={creating || label.trim().length === 0}>
                  {creating ? t('form.creating') : t('form.submit')}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
            <CardContent className="p-6 sm:p-8">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('table.name')}</TableHead>
                    <TableHead>{t('table.status')}</TableHead>
                    <TableHead>{t('table.createdAt')}</TableHead>
                    <TableHead>{t('table.lastUsed')}</TableHead>
                    <TableHead className="w-[120px] text-right">{t('table.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.length === 0 ? (
                    <TableEmpty colSpan={5}>
                      {loading ? t('table.loading') : t('table.empty')}
                    </TableEmpty>
                  ) : (
                    devices.map((device) => (
                      <TableRow key={device.id}>
                        <TableCell>
                          <span className="font-medium text-jq-charcoal">{device.label}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={device.status === 'active' ? 'success' : 'secondary'}>
                            {t(`status.${device.status}`)}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(device.createdAt, i18n.language)}</TableCell>
                        <TableCell>
                          {device.lastUsedAt
                            ? formatDateTime(device.lastUsedAt, i18n.language)
                            : t('table.never')}
                        </TableCell>
                        <TableCell className="text-right">
                          {device.status === 'active' && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              aria-label={t('table.revokeAria', { name: device.label })}
                              onClick={() => setRevokeTarget(device)}
                            >
                              {t('table.revoke')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={!!issued} onOpenChange={(next) => (!next ? setIssued(null) : undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('token.title')}</DialogTitle>
            <DialogDescription>
              {t('token.description', { name: issued?.label ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <p className="mt-4 rounded-sm border border-jq-warning/40 bg-jq-warning/10 px-3 py-2 text-[13px] text-jq-charcoal">
            {t('token.warning')}
          </p>
          <div className="mt-4 flex items-center gap-2">
            <code className="flex-1 break-all rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-3 py-2 font-mono text-[13px] text-jq-charcoal">
              {issued?.token}
            </code>
            <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
              <Copy className="h-4 w-4" strokeWidth={1.75} />
              {copied ? t('token.copied') : t('token.copy')}
            </Button>
          </div>
          <DialogFooter>
            <Button type="button" size="sm" onClick={() => setIssued(null)}>
              {t('token.done')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!revokeTarget}
        onOpenChange={(next) => (!next ? setRevokeTarget(null) : undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('revokeDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('revokeDialog.description', { name: revokeTarget?.label ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRevokeTarget(null)}
              disabled={revoking}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRevoke}
              disabled={revoking}
            >
              {t('revokeDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
